/**
 * `SimulationReadinessPanel` — WP-10 (extracted into a reusable module).
 *
 * The viewer renders this panel in **two** places:
 *
 *   1. Inside the Twin product UX left rail (PHASE-10 baseline), via
 *      a one-line re-export from `frontend/src/app/TwinProductUx.tsx`.
 *      Existing tests (`src/app/__tests__/TwinProductUx.test.tsx`)
 *      continue to import the component from `TwinProductUx` without
 *      any prop or behavior change.
 *   2. Inside the right-rail `ReadinessToolCard` (WP-10) as a shared
 *      summary section. The card composes the same panel + extra
 *      grouped-gap and export affordances.
 *
 * The component is **pure presentation** over a
 * `SimulationReadinessReport`. All readiness logic lives in
 * `readinessModel.ts` so the panel and the ToolCard share one
 * source of truth.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.10
 *   - `docs/results/PHASE-10-SIMULATION-READINESS-COMPLETION.md`
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5
 *     (non-color cues + 40 px touch target).
 */

import { useMemo, type ReactNode } from "react";
import { Button, Chip, Icon, Stack, Text, tokens } from "@/design";
import type {
  SimulationReadinessChecklistItem,
  SimulationReadinessReport,
  UnifiedSceneTreeNode,
} from "@/app/scenePackage";
import { flattenSceneNodes } from "@/app/sceneExplorer";
import {
  countReadinessFacts,
  firstEvidenceNode,
  readinessLevelAppearance,
} from "./readinessModel";

export interface SimulationReadinessPanelProps {
  readonly report: SimulationReadinessReport | null;
  readonly sceneTree?: UnifiedSceneTreeNode | null;
  readonly onSelectNode: (node: UnifiedSceneTreeNode) => void;
}

export function SimulationReadinessPanel({
  report,
  sceneTree,
  onSelectNode,
}: SimulationReadinessPanelProps) {
  const nodesById = useMemo(
    () => new Map(flattenSceneNodes(sceneTree).map((node) => [node.id, node])),
    [sceneTree],
  );
  const fallbackLevel =
    report?.currentReadinessLevel ??
    sceneTree?.readiness.at(-1) ??
    "visualization-ready";
  const appearance = readinessLevelAppearance(fallbackLevel);
  const checklist = report?.domainChecklist ?? [];
  const missing = report?.missingRequiredFields ?? [];
  const candidateImprovements =
    report?.candidateFactsThatCouldImproveReadiness ?? [];
  const { trusted: trustedCount, candidate: candidateCount } =
    countReadinessFacts(report);

  return (
    <section
      aria-label="Simulation readiness"
      style={{
        padding: tokens.space[3],
        borderBottom: tokens.border.thin,
      }}
    >
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Stack direction="row" align="center" gap={2}>
          <Icon name="fact_check" size={24} decorative />
          <div>
            <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
              Simulation readiness
            </Text>
            <Text variant="caption" as="p" style={{ margin: 0 }}>
              {report?.domain ?? "domain pending"}
            </Text>
          </div>
        </Stack>
        <Chip
          icon={appearance.icon}
          label={appearance.label}
          variant={appearance.variant}
        />
      </Stack>

      <p
        style={{
          margin: `${tokens.space[2]}px 0 0`,
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.size.sm,
        }}
      >
        Readiness is certified from trusted topology, units, attributes, and
        provenance. AI candidate facts are shown separately until approved.
      </p>

      {checklist.length > 0 ? (
        <>
          <SectionTitle title="Domain checklist" />
          <Stack direction="column" gap={1}>
            {checklist.map((item) => (
              <div key={item.key} style={miniCardStyle}>
                <Stack
                  direction="row"
                  align="center"
                  justify="space-between"
                  gap={2}
                >
                  <span
                    style={{
                      color: tokens.color.ink.primary,
                      fontSize: tokens.font.size.sm,
                      fontWeight: tokens.font.weight.medium,
                    }}
                  >
                    {item.label}
                  </span>
                  <Chip
                    icon={checklistIcon(item.status)}
                    label={item.status}
                    variant={checklistVariant(item.status)}
                  />
                </Stack>
                <Text
                  variant="caption"
                  as="p"
                  style={{ margin: `${tokens.space[1]}px 0 0` }}
                >
                  {evidenceSummary(
                    item.trustedEvidence?.length ?? 0,
                    item.candidateEvidence?.length ?? 0,
                  )}
                </Text>
              </div>
            ))}
          </Stack>
        </>
      ) : (
        <EmptyText>No simulation readiness report has been loaded yet.</EmptyText>
      )}

      <SectionTitle title="Why not ready" />
      {missing.length > 0 ? (
        <Stack direction="column" gap={1}>
          {missing.slice(0, 6).map((item, index) => (
            <div key={`${item.key ?? "missing"}:${index}`} style={miniCardStyle}>
              <strong
                style={{ display: "block", fontSize: tokens.font.size.sm }}
              >
                {item.label ?? item.key ?? "Missing requirement"}
              </strong>
              <span
                style={{
                  color: tokens.color.ink.secondary,
                  fontSize: tokens.font.size.xs,
                }}
              >
                {item.message ?? "Trusted evidence is required."}
              </span>
            </div>
          ))}
        </Stack>
      ) : (
        <EmptyText>No blocking readiness fields are reported.</EmptyText>
      )}

      <SectionTitle title="Candidate improvements" />
      {candidateImprovements.length > 0 ? (
        <Stack direction="column" gap={1}>
          {candidateImprovements.slice(0, 5).map((item, index) => {
            const targetNode = firstEvidenceNode(
              (item.candidateEvidence ?? []) as readonly Record<
                string,
                unknown
              >[],
              nodesById,
            );
            return (
              <div
                key={`${item.requirement ?? "candidate"}:${index}`}
                style={miniCardStyle}
              >
                <strong
                  style={{ display: "block", fontSize: tokens.font.size.sm }}
                >
                  {item.label ?? item.requirement ?? "Candidate evidence"}
                </strong>
                <span
                  style={{
                    display: "block",
                    color: tokens.color.ink.secondary,
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  {item.message ??
                    "Candidate fact can improve readiness after approval."}
                </span>
                {targetNode ? (
                  <Button
                    variant="ghost"
                    onClick={() => onSelectNode(targetNode)}
                  >
                    Open linked node
                  </Button>
                ) : null}
              </div>
            );
          })}
        </Stack>
      ) : (
        <EmptyText>No AI candidate facts are currently improving readiness.</EmptyText>
      )}

      <Stack
        direction="row"
        gap={1}
        style={{ flexWrap: "wrap", marginTop: tokens.space[3] }}
      >
        <Chip
          icon="verified"
          label={`${trustedCount} trusted facts`}
          variant="success"
        />
        <Chip
          icon="pending"
          label={`${candidateCount} candidate paths`}
          variant="warning"
        />
      </Stack>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Local helpers (private to the panel)
// ---------------------------------------------------------------------------

function SectionTitle({ title }: { readonly title: string }) {
  return (
    <h3
      style={{
        margin: `${tokens.space[3]}px 0 ${tokens.space[2]}px`,
        fontSize: tokens.font.size.sm,
      }}
    >
      {title}
    </h3>
  );
}

function EmptyText({ children }: { readonly children: ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        color: tokens.color.ink.secondary,
        fontSize: tokens.font.size.sm,
      }}
    >
      {children}
    </p>
  );
}

function checklistIcon(
  status: SimulationReadinessChecklistItem["status"],
): Parameters<typeof Icon>[0]["name"] {
  if (status === "satisfied") return "task_alt";
  if (status === "candidate") return "pending";
  return "error";
}

function checklistVariant(
  status: SimulationReadinessChecklistItem["status"],
): Parameters<typeof Chip>[0]["variant"] {
  if (status === "satisfied") return "success";
  if (status === "candidate") return "warning";
  return "error";
}

function evidenceSummary(trusted: number, candidate: number): string {
  if (trusted > 0)
    return `${trusted} trusted evidence link${trusted === 1 ? "" : "s"}`;
  if (candidate > 0)
    return `${candidate} candidate evidence link${candidate === 1 ? "" : "s"} pending approval`;
  return "No linked evidence yet";
}

const miniCardStyle = {
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[0],
  padding: tokens.space[2],
};
